import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { PageShell } from '@/components/primitives/PageShell';
import { PageHeader } from '@/components/primitives/PageHeader';
import { Section } from '@/components/primitives/Section';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import {
  ProfileSchema,
  ChangePasswordSchema,
  type ProfileValues,
  type ChangePasswordValues,
} from './ProfileSchema';
import NotificationPreferences from './NotificationPreferences';

export default function Profile() {
  const { currentUser, updateProfile, changePassword } = useAuth();
  const { currentTenant } = useTenant();

  if (!currentUser) {
    return (
      <PageShell>
        <PageHeader title="Profile" subtitle="Loading account…" />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHeader title="Profile" subtitle="Manage your account details" />

      <Section title="Account information">
        <dl className="grid gap-3 sm:grid-cols-[140px_1fr]">
          <dt className="text-sm text-muted-foreground">Email</dt>
          <dd className="text-sm">{currentUser.email ?? '—'}</dd>

          <dt className="text-sm text-muted-foreground">Role</dt>
          <dd className="flex flex-wrap gap-1.5">
            {currentUser.role && (
              <Badge variant="secondary">{currentUser.role.toLowerCase()}</Badge>
            )}
            {currentUser.roles?.map((r) => (
              <Badge key={r} variant="outline">
                {r.toLowerCase()}
              </Badge>
            ))}
            {!currentUser.role && (!currentUser.roles || currentUser.roles.length === 0) && (
              <span className="text-sm text-muted-foreground">—</span>
            )}
          </dd>

          {currentTenant && (
            <>
              <dt className="text-sm text-muted-foreground">FPA</dt>
              <dd className="text-sm">{currentTenant.name}</dd>
            </>
          )}
        </dl>
      </Section>

      <Section title="Display name" description="How your name appears in the app.">
        <DisplayNameForm
          initial={currentUser.display_name || currentUser.username || ''}
          onSubmit={async (values) => {
            const result = await updateProfile(values.displayName);
            if (result.success) toast.success('Profile updated');
            else toast.error(result.error || 'Failed to update profile');
          }}
        />
      </Section>

      <Section title="Change password" description="Set a new password for your account.">
        <ChangePasswordForm
          onSubmit={async (values) => {
            const result = await changePassword(values.currentPassword, values.newPassword);
            if (result.success) {
              toast.success(result.message || 'Password updated');
              return true;
            }
            toast.error(result.error || 'Failed to change password');
            return false;
          }}
        />
      </Section>

      <Section
        title="Notifications"
        description="Choose which emails you receive from the system. Toggles save instantly."
      >
        <NotificationPreferences />
      </Section>
    </PageShell>
  );
}

interface DisplayNameFormProps {
  initial: string;
  onSubmit: (values: ProfileValues) => Promise<void> | void;
}

function DisplayNameForm({ initial, onSubmit }: DisplayNameFormProps) {
  const form = useForm<ProfileValues>({
    resolver: zodResolver(ProfileSchema),
    defaultValues: { displayName: initial },
    mode: 'onTouched',
  });
  const [submitting, setSubmitting] = useState(false);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(async (v) => {
          setSubmitting(true);
          try {
            await onSubmit(v);
            form.reset({ displayName: v.displayName });
          } finally {
            setSubmitting(false);
          }
        })}
        className="space-y-4"
      >
        <FormField
          control={form.control}
          name="displayName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Display name</FormLabel>
              <FormControl>
                <Input {...field} autoComplete="name" disabled={submitting} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex justify-end">
          <Button type="submit" disabled={submitting || !form.formState.isDirty}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Form>
  );
}

interface ChangePasswordFormProps {
  onSubmit: (values: ChangePasswordValues) => Promise<boolean>;
}

function ChangePasswordForm({ onSubmit }: ChangePasswordFormProps) {
  const form = useForm<ChangePasswordValues>({
    resolver: zodResolver(ChangePasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
    mode: 'onTouched',
  });
  const [submitting, setSubmitting] = useState(false);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(async (v) => {
          setSubmitting(true);
          try {
            const ok = await onSubmit(v);
            if (ok) {
              form.reset({ currentPassword: '', newPassword: '', confirmPassword: '' });
            }
          } finally {
            setSubmitting(false);
          }
        })}
        className="space-y-4"
      >
        <FormField
          control={form.control}
          name="currentPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Current password</FormLabel>
              <FormControl>
                <Input {...field} type="password" autoComplete="current-password" disabled={submitting} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="newPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>New password</FormLabel>
                <FormControl>
                  <Input {...field} type="password" autoComplete="new-password" disabled={submitting} />
                </FormControl>
                <FormDescription>Minimum 8 characters.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirm new password</FormLabel>
                <FormControl>
                  <Input {...field} type="password" autoComplete="new-password" disabled={submitting} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex justify-end">
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Change password'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
